/**
 * Verborgen interne previewpagina voor de contactmails.
 * Niet in de navigatie, niet in de sitemap, noindex.
 * Rendert alleen — er wordt nooit een mail verstuurd.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PACKAGES } from "@/lib/packages";

export const Route = createFileRoute("/email-preview")({
  head: () => ({
    meta: [
      { title: "E-mailpreview (intern) — IT Solutions" },
      { name: "description", content: "Interne preview van de contact- en offertemails." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EmailPreviewPage,
});

type Lang = "nl" | "fr" | "en";
type MailType = "confirm" | "internal" | "followup";

const LANGS: { id: Lang; label: string }[] = [
  { id: "nl", label: "Nederlands" },
  { id: "fr", label: "Français" },
  { id: "en", label: "English" },
];

const TYPES: { id: MailType; label: string }[] = [
  { id: "confirm", label: "Klantbevestiging" },
  { id: "internal", label: "Interne mail (hello@)" },
  { id: "followup", label: "Opvolgmail (na 48u)" },
];

function EmailPreviewPage() {
  const initial =
    typeof window !== "undefined"
      ? (new URLSearchParams(window.location.search).get("pakket") ?? "")
      : "";
  const [pkg, setPkg] = useState(initial);
  const [lang, setLang] = useState<Lang>("nl");
  const [type, setType] = useState<MailType>("confirm");
  const [mobile, setMobile] = useState(false);
  const [showText, setShowText] = useState(false);
  const [formType, setFormType] = useState<"contact" | "audit">("contact");

  const query = useMemo(
    () => `pkg=${encodeURIComponent(pkg)}&lang=${lang}&type=${type}`,
    [pkg, lang, type],
  );
  const src = `/api/preview/email?${query}${showText ? "&format=text" : ""}`;
  const selected = PACKAGES.find((p) => p.id === pkg);
  const subject = subjectFor(type, lang, selected?.name);

  return (
    <main className="min-h-screen bg-background px-6 py-10 text-foreground">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Intern · niet publiek
          </p>
          <h1 className="mt-2 text-3xl font-semibold">E-mailpreview</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Exact dezelfde opbouw als de mails die het contactformulier verstuurt, met
            demo-klantgegevens. Deze pagina verstuurt niets en staat niet in de navigatie of
            sitemap.
          </p>
        </header>

        <div className="mb-6 grid gap-4 rounded border border-border bg-card p-5 md:grid-cols-3">
          <Field label="Pakket">
            <select
              value={pkg}
              onChange={(e) => setPkg(e.target.value)}
              aria-label="Pakket"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Geen pakket (algemene aanvraag)</option>
              {PACKAGES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.price}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Taal">
            <div className="flex gap-2">
              {LANGS.map((l) => (
                <Toggle key={l.id} active={lang === l.id} onClick={() => setLang(l.id)}>
                  {l.label}
                </Toggle>
              ))}
            </div>
          </Field>

          <Field label="Mailtype">
            <div className="flex flex-wrap gap-2">
              {TYPES.map((tp) => (
                <Toggle key={tp.id} active={type === tp.id} onClick={() => setType(tp.id)}>
                  {tp.label}
                </Toggle>
              ))}
            </div>
          </Field>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Toggle active={!mobile} onClick={() => setMobile(false)}>
            Desktop
          </Toggle>
          <Toggle active={mobile} onClick={() => setMobile(true)}>
            Mobiel
          </Toggle>
          <Toggle active={showText} onClick={() => setShowText((v) => !v)}>
            {showText ? "Toon HTML" : "Toon platte tekst"}
          </Toggle>
          {!selected && (
            <>
              <Toggle active={formType === "contact"} onClick={() => setFormType("contact")}>
                Algemeen
              </Toggle>
              <Toggle active={formType === "audit"} onClick={() => setFormType("audit")}>
                GEO-audit
              </Toggle>
            </>
          )}
          <a
            href={
              selected
                ? `/api/preview/quote.pdf?pkg=${encodeURIComponent(selected.id)}&lang=${lang}`
                : `/api/preview/quote.pdf?lang=${lang}&type=${formType}`
            }
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-primary px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            {selected ? "Open offerte-PDF" : "Open aanvraag-PDF"}
          </a>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          Bijlage zoals verstuurd:{" "}
          <code className="rounded bg-muted px-1 py-0.5">
            {`${selected ? "Offerte" : "Aanvraag"}-${demoNameSlug(lang)}-ITS-${new Date().getUTCFullYear()}-#####-${new Date()
              .toISOString()
              .slice(0, 10)}.pdf`}
          </code>{" "}
          — taal {lang.toUpperCase()},{" "}
          {selected
            ? `pakket ${selected.name}`
            : formType === "audit"
              ? "GEO-audit"
              : "algemene aanvraag"}
          .
          <span className="ml-1">
            <code className="rounded bg-muted px-1 py-0.5">#####</code> is het volgnummer van de
            aanvraag; de bestandsnaam bevat contactnaam, offerte-id en datum, en de preview gebruikt
            dezelfde generator en dezelfde bestandsnaamregel.
          </span>
        </p>

        <div className="rounded border border-border bg-card p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Onderwerp
          </p>
          <p className="mb-4 text-sm font-medium">{subject}</p>
          <div
            className="mx-auto overflow-hidden rounded-lg border border-border"
            style={{ maxWidth: mobile ? 390 : "100%" }}
          >
            <iframe
              key={src}
              title="E-mailpreview"
              src={src}
              className="h-[900px] w-full bg-white"
            />
          </div>
        </div>
      </div>
    </main>
  );
}

function demoName(lang: Lang) {
  return lang === "fr" ? "Camille Dubois" : lang === "en" ? "Alex Peeters" : "Sofie Vermeulen";
}

function demoNameSlug(lang: Lang) {
  return demoName(lang)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-");
}

function subjectFor(type: MailType, lang: Lang, pkgName?: string) {
  const who = demoName(lang);
  const company =
    lang === "fr"
      ? "Société Exemple SRL"
      : lang === "en"
        ? "Example Company Ltd"
        : "Voorbeeld Bedrijf BV";
  if (type === "internal") {
    return pkgName
      ? `Offerte-aanvraag ${pkgName} (OFF-2026-0001) — ${who} (${company})`
      : `Nieuwe contactaanvraag — ${who} (${company})`;
  }
  if (type === "followup") {
    if (lang === "fr") {
      return pkgName
        ? `Toujours intéressé par ${pkgName} ? — IT Solutions`
        : "Suivi de votre demande — IT Solutions";
    }
    if (lang === "en") {
      return pkgName
        ? `Still interested in ${pkgName}? — IT Solutions`
        : "Following up on your request — IT Solutions";
    }
    return pkgName
      ? `Nog interesse in ${pkgName}? — IT Solutions`
      : "Even opvolgen van je aanvraag — IT Solutions";
  }
  return pkgName
    ? `Bevestiging offerte-aanvraag ${pkgName} — IT Solutions`
    : "Bedankt voor je aanvraag — IT Solutions";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
